import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Header.css';
import { fetchPosts, fetchReports } from '../api/strapi';
import NavNewsletterSignup from './NavNewsletterSignup';

const services = [
  {
    iconClass: 'fa-regular fa-square-poll-vertical',
    title: 'Primary Research',
    description: 'Insight-driven research & actionable intelligence',
    link: '/services#primary-research-advisory',
    linkText: 'Find out more',
  },
  {
    iconClass: 'fa-regular fa-chess-knight',
    title: 'Positioning, Brand & Market Strategy',
    description: 'Strategic positioning, messaging & growth',
    link: '/services#positioning-brand-market-strategy',
    linkText: 'Find out more',
  },
  {
    iconClass: 'fa-regular fa-icons',
    title: 'Content Marketing & Thought Leadership',
    description: 'High-impact, research-backed content',
    link: '/services#content-marketing-thought-leadership',
    linkText: 'Find out more',
  },
];

export default function Header() {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [recentBlogs, setRecentBlogs] = useState([]);
  const [reports, setReports] = useState([]);
  const [isLoadingReports, setIsLoadingReports] = useState(true);

  const wrapperRef = useRef(null);
  const newsletterDropdownRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    setActiveDropdown(null);
    setMobileOpen(false);
  }, [location]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setActiveDropdown(null);
        setMobileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    fetchPosts()
      .then((posts) => setRecentBlogs(posts.slice(0, 2)))
      .catch((err) => console.error('Error loading blogs:', err));
  }, []);

  useEffect(() => {
    setIsLoadingReports(true);
    fetchReports()
      .then((allFetchedReports) =>
        setReports(Array.isArray(allFetchedReports) ? allFetchedReports : [])
      )
      .catch((err) => {
        console.error('Error loading reports:', err);
        setReports([]);
      })
      .finally(() => setIsLoadingReports(false));
  }, []);

  const toggleDropdown = (name) =>
    setActiveDropdown((current) => (current === name ? null : name));

  const handleNewsletterSignupSuccess = () => {
    setTimeout(() => setActiveDropdown(null), 2500);
  };

  const latestReport = reports.length > 0 ? reports[0] : null;

  return (
    <div ref={wrapperRef}>
      <nav className={`navbarContainer ${mobileOpen ? 'menuOpen' : ''}`}>
        <div className="logoWrapper">
          <Link to="/">
            <svg width="126" height="28" viewBox="0 0 126 28" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g clipPath="url(#clip0_3497_126)">
                <path d="M4 23.9687C4.00001 20.2991 4 10.5763 4 4.02417C8.1806 4.02417 8.49213 4.02417 14.0334 4.02417C19.5748 4.02417 24.0669 8.49139 24.0669 14.002C24.0669 16.666 24.0669 19.4454 24.0669 23.9687" stroke="#053F55" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M28.4305 14C28.4305 19.5107 32.9227 23.9779 38.464 23.9779C42.3946 23.9779 45.7973 21.7302 47.4434 18.4568M28.4305 14C28.4305 8.48944 32.9227 4.02222 38.464 4.02222C44.0053 4.02222 48.4974 8.48944 48.4974 14H28.4305Z" stroke="#053F55" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M52.8605 4C52.8605 4 52.8605 14 62.9162 14C72.972 14 72.972 4 72.972 4" stroke="#053F55" strokeWidth="2.5" strokeLinecap="round"/>
                <path d="M52.8606 23.9999C52.8606 23.9999 52.8606 13.9999 62.9163 13.9999C72.9721 13.9999 72.9721 23.9999 72.9721 23.9999" stroke="#053F55" strokeWidth="2.5" strokeLinecap="round"/>
                <path d="M77.3357 14C77.3357 19.5107 81.8278 23.9779 87.3691 23.9779C91.2997 23.9779 94.7025 21.7302 96.3485 18.4568M77.3357 14C77.3357 8.48944 81.8278 4.02222 87.3691 4.02222C92.9104 4.02222 97.4026 8.48944 97.4026 14H77.3357Z" stroke="#61AECA" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M101.766 4.02212C107.151 4.02212 106.258 4.02212 111.8 4.02212C117.341 4.02212 121.833 8.48935 121.833 13.9999C121.833 19.5106 117.341 23.9778 111.8 23.9778C108.527 23.9778 107.452 23.9778 101.766 23.9778L101.766 18.5676" stroke="#61AECA" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </g>
              <defs>
                <clipPath id="clip0_3497_126">
                  <rect width="121.833" height="24" fill="white" transform="translate(2 2)"/>
                </clipPath>
              </defs>
            </svg>
          </Link>
        </div>

        {/* Mobile Hamburger */}
        <button
          className={`mobileHamburger${mobileOpen ? ' open' : ''}`}
          onClick={() => setMobileOpen((prev) => !prev)}
          aria-label="Toggle menu"
        >
          <span /><span /><span />
        </button>

        {/* Centered Nav Links */}
        <div className={`navLinks${mobileOpen ? ' mobileOpen' : ''}`}>
          <Link to="/" className="navLink">Home</Link>

          <Link to="/services" className="navLink">Services</Link>

          {/* Conditional rendering for Insights/Reports/Blogs based on mobileOpen state */}
          {mobileOpen ? (
            <>
              <Link to="/reports" className="navLink">Reports</Link>
              <Link to="/blog" className="navLink">Blogs</Link>
            </>
          ) : (
            <div
              className={`navLink hasChildren${activeDropdown === 'Content' ? ' open' : ''}`}
              onClick={() => toggleDropdown('Content')}
            >
              Insights
              {/* This subMenu is only relevant for mobile when the Insights dropdown is active */}
              <div className="subMenu">
                {isLoadingReports ? (
                  <p className="loading-text-mobile">Loading report…</p>
                ) : latestReport && latestReport.slug ? (
                  <Link
                    to={`/reports/${latestReport.slug}`}
                    className="megaMenuLink reportItem"
                    style={{ backgroundImage: latestReport.coverUrl ? `url(${latestReport.coverUrl})` : undefined }}
                  >
                    <h4>{latestReport.headline}</h4>
                    {latestReport.summary && <p>{latestReport.summary}</p>}
                  </Link>
                ) : (
                  <p className="noContentMobile">No current report.</p>
                )}

                <div className="submenuTitle">Latest Posts</div>
                {recentBlogs.map((b, i) => (
                  <Link key={i} to={`/blog/${b.slug}`} className="megaMenuLink">
                    {b.title}
                  </Link>
                ))}
              </div>
            </div>
          )}

          <Link to="/about" className="navLink">About</Link>
          <Link to="/contact" className="navLink">Contact</Link>

          {/* Mobile-only Sign up link */}
          {mobileOpen && (
            <Link to="/newsletter" className="navLink navSignUpLink-mobile">
              Join the Network
            </Link>
          )}
        </div>

        {/* Desktop Signup */}
        <div className="signUpContainer">
          {!mobileOpen && (
            <Link
              to="/newsletter" // This is the new path to your NewsletterPage
              id="nav-signup-button"
              className="navLink navSignUpWrapper" // Removed dropdown classes
              // Removed onClick={() => toggleDropdown('SignUp')}
            >
              Join the Network
            </Link>
          )}
        </div>
      </nav>

      {/* Desktop Mega Menus */}
      <div className={`megaMenu servicesMenu${activeDropdown === 'Services' && !mobileOpen ? ' open' : ''}`}>
        <div className="megaMenuContentWrapper">
          <div className="megaMenuContent servicesGrid">
            {services.map((svc, i) => (
              <div key={i} className="megaMenuItem">
                <div className="serviceItemHeader">
                  <i className={`${svc.iconClass} headerIcon`} />
                  <h4>{svc.title}</h4>
                </div>
                <p>{svc.description}</p>
                <Link to={svc.link} className="megaMenuLink">
                  {svc.linkText} <i className="fa-regular fa-arrow-right" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* This mega menu is specifically for the desktop 'Insights' dropdown */}
      <div className={`megaMenu contentMenu${activeDropdown === 'Content' && !mobileOpen ? ' open' : ''}`}>
        <div className="megaMenuContentWrapper">
          <div className="megaMenuContent">
            <div className="megaMenuSection reportSection">
              <div className="megaMenuSectionHeader">
                <h3>Latest Report</h3>
                <Link to="/reports" className="megaMenuSectionLink">
                  View all <i className="fa-regular fa-arrow-right" />
                </Link>
              </div>
              {isLoadingReports ? (
                <p>Loading...</p>
              ) : latestReport && latestReport.slug ? (
                <div
                  className="megaMenuItem reportItem"
                  style={{ backgroundImage: latestReport.coverUrl ? `url(${latestReport.coverUrl})` : undefined }}
                >
                  <h4>{latestReport.headline}</h4>
                  <p>{latestReport.summary}</p>
                  <Link to={`/reports/${latestReport.slug}`} className="megaMenuLink">
                    Read more <i className="fa-regular fa-arrow-right" />
                  </Link>
                </div>
              ) : (
                <p className="noContent">No report</p>
              )}
            </div>
            <div className="megaMenuSection blogsSection">
              <div className="megaMenuSectionHeader">
                <h3>Recent Blogs</h3>
                <Link to="/blog" className="megaMenuSectionLink">
                  View all <i className="fa-regular fa-arrow-right" />
                </Link>
              </div>
              <div className="megaMenuContent blogsGrid">
                {recentBlogs.length > 0 ? (
                  recentBlogs.map((b, i) => (
                    <div key={i} className="megaMenuItem">
                      <h4>{b.title}</h4>
                      <p>{b.summary}</p>
                      <Link to={`/blog/${b.slug}`} className="megaMenuLink">
                        Read more <i className="fa-regular fa-arrow-right" />
                      </Link>
                    </div>
                  ))
                ) : (
                  <p className="noContent">No blogs</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        className={`megaMenu signupMenu${activeDropdown === 'SignUp' && !mobileOpen ? ' open' : ''}`}
        ref={newsletterDropdownRef}
      >
        {activeDropdown === 'SignUp' && !mobileOpen && (
          <NavNewsletterSignup onSignupSuccess={handleNewsletterSignupSuccess} />
        )}
      </div>
    </div>
  );
}